import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { BarcodeIssuesFixRequest } from '@/api/handlers/resources/products/requests';
import type {
    BarcodeFixError,
    FixBarcodeIssuesResponse,
} from '@/api/handlers/resources/products/responses';
import { ProductQueryKeys } from './productQueryKeys';

/**
 * Fixes listed barcodes, all or nothing (`POST products/barcode-issues/fix`,
 * bos-api API-207), and resolves with `{ fixed, generatedBarcodes }`, the
 * labels to print. On success it invalidates every Barcode issues page, every
 * product list (the search and the generated-barcode filter read the new
 * codes) and every product detail, as one call can touch many products.
 *
 * Unwraps the envelope (rulebook §10): a refusal rejects with the
 * `BarcodeFixError`, whose `code` narrows with `isBarcodeFixErrorCode` and
 * whose `itemIndex`, when present, names the item of the body to blame. A
 * refusal applied nothing and invalidates nothing.
 */
export const useFixBarcodeIssuesMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        FixBarcodeIssuesResponse,
        BarcodeFixError,
        BarcodeIssuesFixRequest
    >({
        mutationFn: async (body) => {
            const result = await apiClient.products.fixBarcodeIssues(body);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.barcodeIssueLists(),
            });
            queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.lists(),
            });
            queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.details(),
            });
        },
    });
};
